import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { AbcCore, NavigationInfo } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { ConvertHelper, Environment, UrlHelper } from 'libs/abc/core/helpers';
import { from, Observable, of } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { LoginComponentInfo } from '../login/login-component-info';
import {
  AuthenticationLevelMode,
  AuthenticationProviderType,
  IdentityMode,
  LoginInfo,
  NewUserInfo,
  PasswordRequirementsInfo,
  UserGroupInfo,
} from '../models/login-info.model';
import { ILoginSettings } from '../models/login-settings.model';
import { ChangePasswordInfo } from '../models/password.model';
import { Register } from '../models/register.model';
import { AuthConfigService } from './auth-config.service';
import { LoginSettingsService } from './login-settings.service';
import { ProfileService } from './profile.service';
import { WebApiServiceWithAuthConfig } from './webapi-with-auth-config.service';
// TODO @todo import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class UserService extends WebApiServiceWithAuthConfig {
  public static readonly TOKEN_GRANT_TYPE_PASSWORD = 'password';
  public static readonly TOKEN_GRANT_TYPE_REFRESHTOKEN = 'refresh_token';

  private lastLoginSettingsJson: string;
  private lastLoginInfo: LoginInfo;
  private currentAccountLanguage: string;

  constructor(
    httpClient: HttpClient,
    abcConfigService: AbcConfigService,
    authConfigService: AuthConfigService,
    private loginSettingsService: LoginSettingsService,
    private router: Router,
    private route: ActivatedRoute, // TODO @todo private translate: TranslateService,
    private injector: Injector,
  ) {
    super(httpClient, abcConfigService, authConfigService);
  }

  public async getSystemName(): Promise<string> {
    const current = await this.authConfigService.current.toPromise();
    return current?.systemName;
  }

  private get API_URL_GetToken(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Token;
  }

  private get API_URL_Logout(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_Logout;
  }

  private get API_URL_GetUserInfo(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ReadUserInfo;
  }

  private get API_URL_GetNewUserInfo(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ReadNewUserInfo;
  }

  private get API_URL_UpdateIdentity(): string {
    return UrlHelper.buildUrl(this.abcConfigService.configuration.WebApi.BaseURL, 'api/Account/UpdateIdentity');
  }

  private get API_URL_SendOneTimePassword(): string {
    return UrlHelper.buildUrl(this.abcConfigService.configuration.WebApi.BaseURL, 'api/Account/SendOneTimePassword');
  }

  private get API_URL_Register(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_Register;
  }

  private get API_URL_Change_Password(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ChangePassword;
  }

  public async getServerVersion(): Promise<string> {
    const cfg = await this.authConfigService.current.toPromise();
    return cfg?.version;
  }

  public loginUser(
    userName: string,
    password: string,
    otp?: string,
    otpIdentifier?: string,
    clientIdToReplace?: string,
  ): Observable<void> {
    if (userName?.trim() !== userName) {
      userName = userName?.trim();
    }
    const query: any = {
      grant_type: UserService.TOKEN_GRANT_TYPE_PASSWORD,
      userName: userName,
      password: password,
    };

    return from(this.queryAndStoreLoginSettings(query, true, otp, otpIdentifier, clientIdToReplace));
  }

  public refreshCurrentUser(): Observable<boolean> {
    return from(this.refreshCurrentUserCore());
  }

  private async refreshCurrentUserCore(): Promise<boolean> {
    try {
      const currentSettings = await this.loginSettingsService.getCurrentLoginSettings().toPromise();
      const localSettings = await this.loginSettingsService.getCurrentLoginSettings(localStorage).toPromise();
      if (currentSettings && (!currentSettings.isLoginRequired || currentSettings.refresh_token)) {
        if (!currentSettings.isLoginRequired) {
          return true;
        } else {
          const query: any = {
            grant_type: UserService.TOKEN_GRANT_TYPE_REFRESHTOKEN,
            refresh_token: currentSettings.refresh_token,
          };

          let refreshTokenAlternative: string;
          if (currentSettings.refresh_token !== localSettings?.refresh_token) {
            refreshTokenAlternative = localSettings?.refresh_token;
          }

          await this.queryAndStoreLoginSettings(query, false, undefined, undefined, undefined, refreshTokenAlternative);
          return true;
        }
      } else {
        return false;
      }
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  private async queryAndStoreLoginSettings(
    loginQuery: any,
    noRetry: boolean,
    otp?: string,
    otpIdentifier?: string,
    clientIdToReplace?: string,
    refreshTokenAlternative?: string,
  ): Promise<void> {
    const encodedRequest = Object.keys(loginQuery)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(loginQuery[key]))
      .join('&');

    const queryParams: string[] = [];
    queryParams.push('client_id=' + Environment.getClientId());
    if (this.abcConfigService.configuration.ApplicationIdentifier) {
      queryParams.push('app_id=' + this.abcConfigService.configuration.ApplicationIdentifier);
    }
    if (otp && otpIdentifier) {
      queryParams.push(otpIdentifier + '=' + otp);
    }
    if (clientIdToReplace) {
      queryParams.push('client_id_toreplace=' + clientIdToReplace);
    }
    if (refreshTokenAlternative) {
      queryParams.push('local_token=' + refreshTokenAlternative);
    }

    const url = this.API_URL_GetToken + '?' + queryParams.join('&');
    const response = await this.executeWebApiPOST(url, encodedRequest, true, noRetry).toPromise();
    const loginSettings = await this.loginSettingsService.createLoginSettingsFromLoginResponse(response);
    if (loginSettings.isExpired) {
      throw new Error('Login failed! The received login token is already expired.');
    }
    await this.loginSettingsService.setCurrentLoginSettings(loginSettings).toPromise();
  }

  public get isCurrentlyLoggedInOrAnonymousAllowed(): Observable<boolean> {
    return this.loginSettingsService.hasCurrentLoginSettings;
  }

  public get usesLocalSecurityContext(): Observable<boolean> {
    return this.loginSettingsService.usesLocalSecurityContext;
  }

  public get canLogout(): Observable<boolean> {
    return this.usesLocalSecurityContext.pipe(map((uses) => !uses));
  }

  public getCurrentLoginInfo(doRefresh: boolean = false): Observable<LoginInfo> {
    return (doRefresh ? this.refreshCurrentUser() : of(true)).pipe(
      concatMap(() =>
        this.loginSettingsService.getCurrentLoginSettings().pipe(
          concatMap((loginSettings) => {
            if (loginSettings) {
              const loginSettingsJson = JSON.stringify(loginSettings);
              if (!doRefresh && this.lastLoginInfo && this.lastLoginSettingsJson === loginSettingsJson) {
                return of(this.lastLoginInfo);
              }
              return this.queryCurrentUserInfo(loginSettings).pipe(
                tap((loginInfo) => {
                  this.lastLoginInfo = loginInfo;
                  this.lastLoginSettingsJson = loginSettingsJson;
                }),
              );
            } else {
              return of(null);
            }
          }),
        ),
      ),
    );
  }

  public getCurrentFreshLoginSettings(): Observable<ILoginSettings> {
    return this.refreshCurrentUser().pipe(concatMap(() => this.loginSettingsService.getCurrentLoginSettings()));
  }

  private queryCurrentUserInfo(loginSettings: ILoginSettings): Observable<LoginInfo> {
    return this.executeWebApiPOST(this.API_URL_GetUserInfo, { OutputMode: 'Json' }).pipe(
      map((response) =>
        loginSettings.toLoginInfo(
          <string>response['Id'],
          <string>response['Name'],
          <string>response['DisplayText'],
          ConvertHelper.toBoolean(response['IsAdmin']),
          ConvertHelper.toBoolean(response['NeedsPasswordChange']),
          <string>response['EmailAddress'],
          <AuthenticationLevelMode>response['SecondFactorMode'],
          <IdentityMode>response['EmailIdentityMode'],
          <IdentityMode>response['PhoneIdentityMode'],
          <IdentityMode>response['AppTotpIdentityMode'],
          this.createPasswordRequirements(response['PasswordRequirements']),
        ),
      ),
    );
  }

  public queryNewUserInfo(groupNames: string[]): Observable<NewUserInfo> {
    return this.executeWebApiPOST(this.API_URL_GetNewUserInfo, { OutputMode: 'Json', GroupNames: groupNames }).pipe(
      map((response) => {
        const o = ConvertHelper.arrayBufferToObject(response);
        let groups: UserGroupInfo[];
        let passwordRequirements: PasswordRequirementsInfo;
        if (o && Array.isArray(o['Groups'])) {
          groups = [];
          for (const g of o['Groups']) {
            groups.push(new UserGroupInfo(g['Type'], this.createPasswordRequirements(g['PasswordRequirements'])));
          }
        }
        if (o && o['PasswordRequirements']) {
          passwordRequirements = this.createPasswordRequirements(o['PasswordRequirements']);
        }
        return new NewUserInfo(groups, passwordRequirements);
      }),
    );
  }

  public registerUser(Name: string, Password: string, ConfirmPassword: string): Observable<any> {
    const _queryRegister: Register = {
      Name: Name,
      Password: Password,
      ConfirmPassword: ConfirmPassword,
    };

    return this.executeWebApiPOST(this.API_URL_Register, _queryRegister);
  }

  public ChangeUserPassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<ArrayBuffer> {
    const _queryChangeUserPassword = new ChangePasswordInfo();
    _queryChangeUserPassword.OldPassword = oldPassword;
    _queryChangeUserPassword.NewPassword = newPassword;
    _queryChangeUserPassword.ConfirmPassword = confirmPassword;

    return this.executeWebApiPOST(this.API_URL_Change_Password, _queryChangeUserPassword);
  }

  private createPasswordRequirements(requirements: any): PasswordRequirementsInfo | undefined {
    if (!requirements) {
      return undefined;
    }
    return new PasswordRequirementsInfo(
      this.toNumber(requirements.MinLength),
      this.toNumber(requirements.MaxLength),
      this.toBoolean(requirements.Lower),
      this.toBoolean(requirements.Upper),
      this.toBoolean(requirements.Digit),
      this.toBoolean(requirements.Symbol),
    );
  }

  private toNumber(number: any): number {
    if (number) {
      if (typeof number === 'number') {
        return number;
      } else {
        return parseInt(number);
      }
    }
    return 0;
  }

  private toBoolean(v: any): boolean {
    return ConvertHelper.toBoolean(v);
  }

  public logoutCurrentUser(doNavigateToInitPage?: boolean): Observable<boolean> {
    return this.executeWebApiPOST(this.API_URL_Logout, null).pipe(
      concatMap((result) => {
        this.injector.get(ProfileService)?.clearProfilesForCurrentUser();
        return this.loginSettingsService
          .removeCurrentLoginSettings()
          .pipe(concatMap((_) => (doNavigateToInitPage ? this.navigateToInitPage() : of(true))));
      }),
    );
  }

  public createLoginPageNavigationInfo(router?: Router, route?: ActivatedRoute): NavigationInfo {
    return LoginComponentInfo.createLoginPageNavigationInfo(router ?? this.router, route ?? this.route);
  }

  public navigateToLoginPage(router?: Router, route?: ActivatedRoute): Promise<boolean> {
    return LoginComponentInfo.navigateToLoginPage(router ?? this.router, route ?? this.route);
  }

  public createLoginPageUrlTree(router?: Router, route?: ActivatedRoute): UrlTree {
    return LoginComponentInfo.createLoginPageUrlTree(router ?? this.router, route ?? this.route);
  }

  private navigateToInitPage(router?: Router, route?: ActivatedRoute): Observable<boolean> {
    return this.currentAuthConfiguration.pipe(
      concatMap((cfg) => {
        return from(this.navigateToLoginPage(router, route));
      }),
    );
  }

  public async sendOneTimePassword(providerType: AuthenticationProviderType): Promise<void> {
    await this.executeWebApiPOST(this.API_URL_SendOneTimePassword, { ProviderType: providerType }).toPromise();
  }

  public async updateIdentity(
    providerType: AuthenticationProviderType,
    identityValue: string,
    confirmationCode?: string,
  ): Promise<string> {
    const data = await this.executeWebApiPOST(this.API_URL_UpdateIdentity, {
      ProviderType: providerType,
      IdentityValue: identityValue,
      ConfirmationCode: confirmationCode,
    }).toPromise();

    return data as unknown as string;
  }

  public async getAccountLanguage(): Promise<string> {
    if (!this.currentAccountLanguage) {
      this.currentAccountLanguage = ConvertHelper.arrayBufferToObject<{ LocaleIdentifier: string }>(
        await this.injector
          .get(AbcCore)
          .data.executeWebApiPOSTMethod(this.abcConfigService.configuration.WebApi.ServiceURLs.Account_GetLanguage, {})
          .toPromise(),
      )?.LocaleIdentifier;

      this.injector.get(ResourceManagerService).changeLanguage(this.currentAccountLanguage);
    }
    return this.currentAccountLanguage;
  }

  public async setAccountLanguage(language: string): Promise<boolean> {
    try {
      ConvertHelper.arrayBufferToObject<Object>(
        await this.injector
          .get(AbcCore)
          .data.executeWebApiPOSTMethod(this.abcConfigService.configuration.WebApi.ServiceURLs.Account_SetLanguage, {
            LocaleIdentifier: language,
          })
          .toPromise(),
      );
      ResourceManagerService.setCurrentUserLanguage(language);
      return true;
    } catch {
      return false;
    }
  }

  public async setDocumentElementLang(): Promise<void> {
    const accountLanguage = await this.getAccountLanguage();
    if (accountLanguage) {
      document.documentElement.lang = accountLanguage;
    }
  }
}
